import { ajouterJours } from './calendrier'
import type { Certitude, StatutEcheance } from './domaine'
import { formaterMontant } from './montant'

// Flux iCal (RFC 5545) des échéances : évènements sur la journée, fenêtre comprise.

export type EvenementIcal = {
  id: string
  entite: string
  libelle: string
  periode: string
  date: string
  finFenetre: string | null
  certitude: Certitude
  statut: StatutEcheance
  montantPrevuCts: number | null
  devise: string
  lien?: string
}

function echapper(texte: string): string {
  return texte
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

const encodeur = new TextEncoder()

/** Replie une ligne en morceaux de 75 octets au plus, sans couper un caractère. */
function replier(ligne: string): string {
  const morceaux: string[] = []
  let courant = ''
  let octets = 0
  for (const car of ligne) {
    const taille = encodeur.encode(car).length
    const limite = morceaux.length === 0 ? 75 : 74 // les suites commencent par une espace
    if (octets + taille > limite) {
      morceaux.push(courant)
      courant = ''
      octets = 0
    }
    courant += car
    octets += taille
  }
  morceaux.push(courant)
  return morceaux.join('\r\n ')
}

const jourIcal = (jour: string) => jour.replaceAll('-', '')

function horodatage(instant: Date): string {
  return instant
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '')
}

export function genererIcal(
  evenements: EvenementIcal[],
  options: { nom: string; maintenant: Date },
): string {
  const l: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//admin-pmvb//Echeancier//FR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${echapper(options.nom)}`,
    'X-WR-TIMEZONE:Europe/Paris',
  ]
  for (const e of evenements) {
    const prefixe = e.statut === 'faite' ? 'Fait : ' : ''
    const description = [
      `Période ${e.periode}`,
      e.montantPrevuCts !== null
        ? `Montant prévu ${formaterMontant(e.montantPrevuCts, e.devise)}`
        : null,
      e.certitude === 'a_confirmer' ? 'Date à confirmer' : null,
      e.lien ?? null,
    ]
      .filter(Boolean)
      .join('\n')
    l.push(
      'BEGIN:VEVENT',
      `UID:${e.id}@admin-pmvb`,
      `DTSTAMP:${horodatage(options.maintenant)}`,
      `DTSTART;VALUE=DATE:${jourIcal(e.date)}`,
      `DTEND;VALUE=DATE:${jourIcal(ajouterJours(e.finFenetre ?? e.date, 1))}`,
      `SUMMARY:${echapper(`${prefixe}${e.entite} : ${e.libelle}, ${e.periode}`)}`,
      `DESCRIPTION:${echapper(description)}`,
      `STATUS:${e.statut === 'sans_objet' ? 'CANCELLED' : e.certitude === 'a_confirmer' ? 'TENTATIVE' : 'CONFIRMED'}`,
      'TRANSP:TRANSPARENT',
      ...(e.lien ? [`URL:${e.lien}`] : []),
      'END:VEVENT',
    )
  }
  l.push('END:VCALENDAR')
  return l.map(replier).join('\r\n') + '\r\n'
}
